import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import { createResourceLocatorString } from '../../util/routes';
import { createSlug } from '../../util/urlHelpers';
import { onCheckUserStripeAccountLinked } from '../../util/api';
import { DEFAULT_BOOKING_FREE_PROCESS_NAME } from '../../transactions/transaction';
import { isWithinNinetyDays } from '../../util/data';
import { setInitialValues } from './CheckoutPage.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import { getItemWithLargestBookingPeriod } from '../CartPage/CartHelpers';
import { checkExpiredRentalDates } from '../../util/cartHelpers';
import { clearMiniCartDismissed } from '../../util/sessionStorageManager';

/**
 * Shared helper to start checkout from cart items.
 * Used by CartPage and FloatingCheckoutWidget.
 */
export const startCheckoutFromItems = async options => {
  const {
    items,
    activeAuthorId,
    lineItems,
    fetchLineItemsInProgress,
    setItemsInProgress,
    removeFromCartInProgress,
    currentUser,
    routeConfiguration,
    dispatch,
    history,
  } = options || {};

  if (!items || !items.length) {
    return false;
  }

  // A cart whose start day has passed can't be checked out - the checkout page's
  // speculative transaction would only fail on the past dates (client bug report
  // 2026-09-18: an expired side cart reached an error page). Every surface that
  // calls this shows its own expired notice and disables its button; this is the
  // backstop so no path - including a stale button state - can navigate there.
  if (checkExpiredRentalDates(items).hasExpiredDates) {
    return false;
  }

  const hasOngoingLineItemFetch = Object.values(fetchLineItemsInProgress || {}).some(Boolean);
  if (setItemsInProgress || removeFromCartInProgress || hasOngoingLineItemFetch) {
    return;
  }

  // Item with largest booking period should be base item
  const baseItem = getItemWithLargestBookingPeriod(items);
  if (!baseItem) {
    return;
  }

  const withinNinetyDays = isWithinNinetyDays({
    timeZone: baseItem?.listing?.attributes?.availabilityPlan?.timezone,
    bookingEnd: baseItem.bookingDates.endDate,
  });

  const listing = baseItem.listing;
  const { isAuthorStripeAccountLinked } = await onCheckUserStripeAccountLinked({
    userId: listing.author.id.uuid,
  });

  if (!isAuthorStripeAccountLinked) {
    Object.assign(listing.attributes.publicData, {
      transactionProcessAlias: DEFAULT_BOOKING_FREE_PROCESS_NAME + '/release-1',
    });
  }

  const orderData = items.map(i => {
    const {
      usageMultipleValues,
      selectedFeeTypes, // NEW: Use selectedFeeTypes
      otherOptionalFees, // Keep for backward compatibility
      timeZone = 'Etc/UTC',
      bookingDates,
      quantity,
      listing: itemListing,
      authorId,
      pickupAtOwnerLocation,
      returnAtOwnerLocation,
      id,
      queryID,
    } = i;

    const { startDate, endDate } = bookingDates || {};

    const bookingDatesMaybe =
      startDate && endDate
        ? {
          bookingDates: {
            bookingStart: moment(startDate)
              .tz(timeZone)
              .toDate(),
            bookingEnd: moment(endDate)
              .tz(timeZone)
              .toDate(),
          },
        }
        : {};

    const seatsMaybe = quantity
      ? {
        seats: Number.parseInt(quantity, 10),
      }
      : {};

    const listingMaybe = itemListing ? { listing: itemListing } : {};

    const lineItemsMaybe =
      lineItems && authorId && lineItems[authorId]
        ? { lineItems: lineItems[authorId] }
        : {};

    const packageMaybe = i?.package ? { package: i.package } : {};
    const authorIdMaybe = authorId ? { authorId } : {};
    const queryIdMaybe = queryID ? { queryID } : {};

    return {
      ...bookingDatesMaybe,
      ...seatsMaybe,
      ...listingMaybe,
      ...lineItemsMaybe,
      ...packageMaybe,
      ...authorIdMaybe,
      ...queryIdMaybe,
      usageMultipleValues,
      selectedFeeTypes: selectedFeeTypes || otherOptionalFees || [], // NEW: Prefer selectedFeeTypes, fallback to otherOptionalFees
      pickupAtOwnerLocation,
      returnAtOwnerLocation,
      id,
    };
  });

  const returnCareInstructions = items
    .map(i => {
      const { listing: itemListing } = i;
      const instructions = itemListing?.attributes?.publicData?.return_care_instructions;
      const title = itemListing?.attributes?.title;
      if (!Array.isArray(instructions) || !title) return null;
      return {
        title,
        instructions,
      };
    })
    .filter(Boolean);

  const initialValues = {
    listing,
    withinNinetyDays,
    orderData,
    confirmPaymentError: null,
    lineItems: lineItems && lineItems[activeAuthorId],
    returnCareInstructions,
  };

  // Always save to sessionStorage for abandoned checkout tracking
  const saveToSessionStorage = true;

  dispatch(setInitialValues(initialValues, saveToSessionStorage));
  dispatch(initializeCardPaymentData());

  // Clear dismissal state when starting new checkout
  clearMiniCartDismissed();

  history.push(
    createResourceLocatorString(
      'CheckoutPage',
      routeConfiguration,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    )
  );
};

/**
 * Start checkout from abandoned checkout data.
 * Used when user returns to complete an abandoned checkout session.
 */
export const startCheckoutFromAbandonedData = async options => {
  const {
    abandonedCheckout,
    cartItems,
    isAddingToCartInProgress,
    currentUser,
    routeConfiguration,
    dispatch,
    history,
  } = options || {};

  if (!abandonedCheckout || !cartItems || isAddingToCartInProgress) {
    return;
  }

  const { listing, orderData, lineItems, withinNinetyDays, returnCareInstructions } = abandonedCheckout;

  if (!listing || !orderData) {
    return;
  }

  // Check author's Stripe status
  const { isAuthorStripeAccountLinked } = await onCheckUserStripeAccountLinked({
    userId: listing.author.id.uuid,
  });

  if (!isAuthorStripeAccountLinked) {
    Object.assign(listing.attributes.publicData, {
      transactionProcessAlias: DEFAULT_BOOKING_FREE_PROCESS_NAME + '/release-1',
    });
  }

  // Match abandoned checkout items with cart items to get IDs
  const orderDataArray = Array.isArray(orderData) ? orderData : [orderData];
  const orderDataWithIds = orderDataArray.map(abandonedItem => {
    // Try to find matching cart item
    let matchingCartItem = null;

    const abandonedListingId = abandonedItem.listing?.id?.uuid || listing?.id?.uuid;
    const abandonedPackage = abandonedItem.package;
    const abandonedDates = abandonedItem.bookingDates || {};
    const abandonedStart = abandonedDates.bookingStart || abandonedDates.startDate;
    const abandonedEnd = abandonedDates.bookingEnd || abandonedDates.endDate;

    // Find matching cart item by listingId, package, and dates
    matchingCartItem = cartItems.find(cartItem => {
      if (cartItem.listingId !== abandonedListingId) {
        return false;
      }

      const cartStart = cartItem.bookingDates?.startDate;
      const cartEnd = cartItem.bookingDates?.endDate;

      if (!cartStart || !cartEnd || !abandonedStart || !abandonedEnd) {
        return false;
      }

      // Compare dates (normalize to same format)
      return (
        moment(cartStart).isSame(abandonedStart) &&
        moment(cartEnd).isSame(abandonedEnd)
      );
    });

    // Include cart item ID if found, otherwise keep original item without ID
    return {
      ...abandonedItem,
      ...(matchingCartItem?.id ? { id: matchingCartItem.id } : {}),
    };
  });

  const initialValues = {
    listing,
    withinNinetyDays,
    orderData: orderDataWithIds,
    confirmPaymentError: null,
    lineItems,
    returnCareInstructions,
  };

  // Always save to sessionStorage for abandoned checkout tracking
  const saveToSessionStorage = true;

  dispatch(setInitialValues(initialValues, saveToSessionStorage));
  dispatch(initializeCardPaymentData());

  // Clear dismissal state when starting checkout
  clearMiniCartDismissed();

  history.push(
    createResourceLocatorString(
      'CheckoutPage',
      routeConfiguration,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    )
  );
};
